import React, { useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import { Slide as SlideAnimation } from 'react-awesome-reveal';
import { HeaderV7 } from '../HeaderV7';
import { HeaderV6 } from '../HeaderV6';
import { TimerV2 } from '../TimerV2';
import { Button } from '../Button';
import { QuestionAnswerSlideV8 } from '../QuestionAnswerSlideV8';
import { QuestionGridSlideV1 } from '../QuestionGridSlideV1';
import { QuestionClaritySlideV1 } from '../QuestionClaritySlideV1';
import { QuestionSignSlideV1 } from '../QuestionSignSlideV1';
import { QuestionSoundSlideV1 } from '../QuestionSoundSlideV1';
import { QuestionSmellsSlideV1 } from '../QuestionSmellsSlideV1';
import { StartSlideV20 } from '../StartSlideV20';
import { PauseSlide } from '../PauseSlide';
import { PlanLoaderSlideV11 } from '../PlanLoaderSlideV11';
import { EmailSlideV10 } from '../EmailSlideV10';
import { NameSlideV4 } from '../NameSlideV4';
import { ResultSlideV3 } from '../ResultSlideV3';
import { SaleV40 } from '../SaleV40';
import { FooterV3 } from '../FooterV3';
import { AgreementTextV6 } from '../AgreementTextV6';
import { useQuiz } from '../../shared/use-quiz';
import { useUserProperties } from '../../shared/use-user-properties';
import { useLegalInfo } from '../../shared/use-legal-info';
import { useSlideEventsV3 } from '../../shared/use-slide-events-v3';
import { isExpiredPromo } from '../../shared/promo-helpers';
import {
    EMAIL_SLIDE,
    NAME_SLIDE,
    PAUSE_SLIDE,
    PLAN_LOADER_SLIDE,
    QUESTION_ANSWER_SLIDE_COMPONENT,
    RESULT_SLIDE,
    START_SLIDE
} from '../../shared/quiz-component-types';
import { preloadImages } from '../../../lib/shared/preload-images';
import emojiSpriteSrc from '../../assets/images/emoji-sprites/emoji-sprite-v59.png';
import footerLogoSrc from '../../assets/images/boundly-logo-pink-light.png';
import styles from './CommonPrelanderV53.css';
import '../../assets/images/emoji-sprites/emoji-sprite-v59.css';

const cx = classnames.bind(styles);

const SPRITE_VERSION = 'v59';
const IMAGES = [emojiSpriteSrc];

const QUESTION_SLIDES = {
    text: QuestionAnswerSlideV8,
    grid: QuestionGridSlideV1,
    clarity: QuestionClaritySlideV1,
    sign: QuestionSignSlideV1,
    sound: QuestionSoundSlideV1,
    smells: QuestionSmellsSlideV1
};

const SLIDE_COMPONENTS = {
    [START_SLIDE]: StartSlideV20,
    [PAUSE_SLIDE]: PauseSlide,
    [PLAN_LOADER_SLIDE]: PlanLoaderSlideV11,
    [EMAIL_SLIDE]: EmailSlideV10,
    [NAME_SLIDE]: NameSlideV4,
    [RESULT_SLIDE]: ResultSlideV3
};

const DELAY = 350;

function CommonPrelanderV53({
    initialPrelander,
    quizSlides
}) {
    const {
        currentSlideIndex,
        currentSlide,
        results,
        submitAnswer,
        submitTemporaryAnswer,
        temporaryAnswer,
        finished,
        nextSlide,
        backSlide,
        setEmail,
        setExpiryTimestampPromo
    } = useQuiz(quizSlides, initialPrelander);
    const [isExpired, setIsExpired] = useState(isExpiredPromo(results.expiryTimestampPromo));

    const quizSlidesIndexing = quizSlides.filter(slide => !slide.isNotIndexing);
    const isQuestionSlide = currentSlide.component === QUESTION_ANSWER_SLIDE_COMPONENT;
    const isStartSlide = currentSlide.component === START_SLIDE;
    const isEmailSlide = currentSlide.component === EMAIL_SLIDE;
    const indexedSlideNumber = quizSlidesIndexing.indexOf(currentSlide) + 1;
    const { legalLinks, legalEntity } = useLegalInfo();
    const [selectedSubscriptionData, selectSubscription] = useState({});
    const [merchantData, setMerchantData] = useState({});
    const hasSmellsAnswer = Boolean(temporaryAnswer?.good?.length || temporaryAnswer?.bad?.length);
    const isContinueDisabled = currentSlide.kind === 'smells' && !hasSmellsAnswer;

    useUserProperties(initialPrelander);

    useEffect(() => {
        preloadImages(IMAGES);
    }, []);

    const { logQuizLifecycle } = useSlideEventsV3({ currentSlideIndex: indexedSlideNumber, currentSlide, quizSlides });

    const handleAnswerWithTransition = answer => () => {
        logQuizLifecycle(quizSlidesIndexing.length === indexedSlideNumber, answer);
        submitAnswer(answer);
    };

    const handleContinue = () => {
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });
        logQuizLifecycle(quizSlidesIndexing.length === indexedSlideNumber, temporaryAnswer);

        if (currentSlideIndex === quizSlides.length - 1) {
            setExpiryTimestampPromo();
            setIsExpired(false);
        }

        nextSlide(temporaryAnswer);
    };

    // the paywall's header is the live HeaderV6, as in CommonPrelanderV49: the logo, then the timer and «Get my plan»
    const renderLeftNavigation = () => (
        <>
            <TimerV2
                expiryTimestamp={isExpired ? null : results.expiryTimestampPromo}
                onExpire={() => setIsExpired(true)}
                className={styles.headerTimer}
            />
            <Button
                label='Get my plan'
                type='sunset'
                className={styles.buttonMyPlan}
                onClick={() => document.getElementById('subscription-start-block')?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
            />
        </>
    );

    const renderHeader = () => {
        if (merchantData?.merchant) {
            return null;
        }

        if (finished && Boolean(results.email)) {
            if (selectedSubscriptionData.plan) {
                return null;
            }

            return (
                <HeaderV6
                    renderLeftNavigation={renderLeftNavigation}
                    className={cx('header', 'withBorder', { withoutPromo: isExpired, withPromo: !isExpired })}
                    isFinished
                    url={window.location.pathname}
                />
            );
        }

        const hasBackButton = !finished && Boolean(currentSlideIndex) && !currentSlide?.isNotBackButton;

        return (
            <HeaderV7
                url={window.location.pathname}
                hasBackButton={hasBackButton}
                onBack={backSlide}
                questionNav={isQuestionSlide ? {
                    title: currentSlide.props?.category,
                    current: indexedSlideNumber,
                    total: quizSlidesIndexing.length
                } : null}
            />
        );
    };

    const renderFooter = () => {
        if (finished) {
            if (Boolean(results.email) && !selectedSubscriptionData.plan) {
                return <FooterV3 legalEntity={legalEntity} legalLinks={legalLinks} logoSrc={footerLogoSrc} />;
            }

            return null;
        }

        if (Boolean(currentSlide.continueButton)) {
            return (
                <SlideAnimation
                    delay={500}
                    duration={250}
                    triggerOnce
                    direction='up'
                    className={styles.slideFooter}
                    key={currentSlide.id}
                >
                    <div className={styles.slideFooterWrapper}>
                        <Button
                            type='sunset'
                            label={currentSlide.continueButton.label || 'Continue'}
                            onClick={handleContinue}
                            disabled={isContinueDisabled}
                            inSlide
                        />
                    </div>
                </SlideAnimation>
            );
        }

        return null;
    };

    const renderContent = () => {
        if (finished) {
            return (
                <SaleV40
                    data={results}
                    expiryTimestamp={results.expiryTimestampPromo}
                    isExpired={isExpired}
                    initialPrelander={initialPrelander}
                    selectSubscription={selectSubscription}
                    selectedSubscriptionData={selectedSubscriptionData}
                    merchantData={merchantData}
                    setMerchantData={setMerchantData}
                    legalLinks={legalLinks}
                />
            );
        }

        const Slide = isQuestionSlide ? QUESTION_SLIDES[currentSlide.kind] : SLIDE_COMPONENTS[currentSlide.component];
        const resultData = (temporaryAnswer?.id || temporaryAnswer?.answerIds)
            ? temporaryAnswer
            : results.userResponses.find(userResponse => userResponse.questionId === currentSlide.id);

        return (
            <div
                key={currentSlideIndex}
                className={cx('slide', {
                    withStartSlide: isStartSlide,
                    withEmailSlide: isEmailSlide,
                    withFullHeightSlide: [PAUSE_SLIDE, NAME_SLIDE, PLAN_LOADER_SLIDE].includes(currentSlide.component)
                })}
            >
                <Slide
                    {...currentSlide.props}
                    spriteVersion={SPRITE_VERSION}
                    submitAnswer={handleAnswerWithTransition}
                    submitTemporaryAnswer={submitTemporaryAnswer}
                    userResponse={resultData}
                    onContinue={handleContinue}
                    id={currentSlide.id}
                    results={results}
                    delay={DELAY}
                    setEmail={setEmail}
                    legalLinks={legalLinks}
                />
                {isStartSlide && <div className={cx('agreement')}><AgreementTextV6 legalLinks={legalLinks} /></div>}
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={cx({ stickyHeader: finished && Boolean(results.email) && !selectedSubscriptionData.plan })}>
                {renderHeader()}
            </div>
            <main
                key={finished}
                className={cx('main', { animated: finished })}
            >
                {renderContent()}
            </main>
            {renderFooter()}
        </div>
    );
}

export {
    CommonPrelanderV53
};
